import React, { useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { formatDateFromApi } from '../../../utils/attendanceDateTime';
import offerLettersService from '../../../services/offerLettersService';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const DocumentsSection = () => {
  const { employee, documents, letters, reloadDocuments } = useEmployeeSelfService();

  useEffect(() => {
    if (employee?.id) reloadDocuments();
  }, [employee?.id, reloadDocuments]);

  const openDoc = async (doc) => {
    if (doc.download_url && employee?.id) {
      const baseURL = API_BASE.replace(/\/api$/, '');
      const token = localStorage.getItem('erp_auth_token') || localStorage.getItem('auth_token');
      const url = `${baseURL}${doc.download_url}${doc.download_url.includes('?') ? '&' : '?'}token=${token}`;
      window.open(url, '_blank', 'noopener');
      return;
    }
    if (doc.document_type === 'offer_letter' && doc.id?.startsWith('offer-')) {
      const letterId = String(doc.id).replace('offer-', '');
      await offerLettersService.downloadOfferLetter(letterId);
      return;
    }
    if (doc.file_path) {
      const url = doc.file_path.startsWith('http')
        ? doc.file_path
        : `${API_BASE.replace(/\/api$/, '')}/${doc.file_path.replace(/^\//, '')}`;
      window.open(url, '_blank', 'noopener');
    }
  };

  const allDocs = [
    ...documents.map((d) => ({ ...d, category: 'document' })),
    ...(letters.offer_letters || []).map((l) => ({
      id: `offer-${l.id}`,
      document_name: `Offer Letter — ${l.position || l.employee_name || l.id}`,
      document_type: 'offer_letter',
      file_path: l.file_path,
      download_url: l.download_url || `/offer-letters/${l.id}/download`,
      uploaded_at: l.created_at,
      category: 'letter',
    })),
    ...(letters.increment_letters || []).map((l) => ({
      id: `inc-${l.id}`,
      document_name: `Increment Letter — ${l.effective_date || l.id}`,
      document_type: 'increment_letter',
      file_path: l.file_path,
      uploaded_at: l.created_at,
      category: 'letter',
    })),
  ];

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">My Documents</h2>
          <p className="text-sm text-muted-foreground">Same documents as Employee Management profile</p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={reloadDocuments}>
          Refresh
        </Button>
      </div>

      <div className="grid gap-3">
        {allDocs.map((doc) => (
          <div key={doc.id} className="bg-card border rounded-lg p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Icon name="FileText" size={20} className="text-primary shrink-0" />
              <div className="min-w-0">
                <p className="font-medium truncate">{doc.document_name || doc.document_type}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {doc.document_type?.replace(/_/g, ' ')}
                  {doc.uploaded_at ? ` • ${formatDateFromApi(doc.uploaded_at)}` : ''}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" iconName="Download" onClick={() => openDoc(doc)}>
              Open
            </Button>
          </div>
        ))}
        {allDocs.length === 0 && (
          <p className="text-center py-12 text-muted-foreground">No documents on file</p>
        )}
      </div>
    </div>
  );
};

export default DocumentsSection;
